import { Assert } from "./Assert";

/**
 * Utility functions to help deal with nullability in various ways.
 * Especially useful for accessing codegenerated dto objects, which tend to be declared as more nullable than
 * how we intend to work with them.
 */
export class NullUtils {
  /**
   * Take a nullable object, Assert it's not null, and return the object with a non-null type.
   */
  static assertIfNull(x, additionalMessage = null) {
    if (x === null) return Assert.unexpected(`Unexpected null: <${additionalMessage}>`);else return x;
  }

  /**
   * Take a nullable object, Assert it's not null, and return the object with a non-null type.
   */
  static assertIsDefined(x, additionalMessage = null) {
    if (x == null) return Assert.unexpected(`Unexpected null or undefined: <${additionalMessage}>`);else return x;
  }

  /**
   * Take a nullable object, Assert it's not null or undefined, and return the object with a non-null non-undefined type.
   */
  static assertDefined(x, additionalMessage = null) {
    if (x == null) return Assert.unexpected(`Unexpected null or undefined: <${additionalMessage}>`);else return x;
  }
  static getRequiredPropertyValue(object, property) {
    const propertyValue = object[property];
    if (propertyValue == null) {
      Assert.unexpected(`Expected ${String(property)} to be defined on ${JSON.stringify(object, null, 2)}`);
    }
    return propertyValue;
  }

  /**
   * Take an index and array of nullable elements, Assert the element is not null, and return the element with a non-null type.
   */
  static getElementAsserted(a, i, additionalMessage = null) {
    let ret = a[i];
    if (ret == null) return Assert.unexpected(`Unexpected null in array element: <${additionalMessage}>`);else return ret;
  }

  /**
   * Take a nullable object, and either throw if null, or return the object with a non-null type.
   */
  static throwIfNull(x) {
    return this.throwIfNullWithMessage(x, "Unexpected null");
  }
  static throwIfNullWithMessage(x, errorMessage) {
    {
      if (x === null) throw new Error(errorMessage);else return x;
    }
  }

  /**
   * Take a nullable object, and return it as undefined if it is null.
   */
  static nullToUndefined(x) {
    if (x === null) return undefined;else return x;
  }

  /**
   * Take an undefined-able object, and return it as null if it is undefined.
   */
  static undefinedToNull(x) {
    if (x === undefined) return null;else return x;
  }

  /**
   * Convert an Array<T | null> into an Array<T>, by stripping any null elements, and asserting if any are null.
   * Helpful for cautiously scrubbing DTO arrays of nulls when none are expected.
   */
  static assertNoNullElements(arr) {
    let ret = [];
    arr.forEach((value, index) => {
      if (value != null) ret.push(value);else return Assert.unexpected(`Unexpected null in array element`);
    });
    return ret;
  }

  /**
   * Typesafe and compact way for replacing null/undefined with a default value.
   * For expressions that are long, this is more concise than using the ternary
   * operator directly in checking for null or undefined.
   */
  static applyDefault(optionalValue, defaultValue) {
    return optionalValue == null ? defaultValue : optionalValue;
  }

  /**
   * Executes the given mapper function if the given value is not null or undefined and returns its result.
   * Otherwise, returns undefined.
   */
  static mapIfPresent(optionalValue, mapper) {
    if (optionalValue == null) {
      return undefined;
    }
    return mapper(optionalValue);
  }
}
//# sourceMappingURL=NullUtils.js.map
